import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useStore } from 'hooks';
import { StationService } from 'services';
import Menu from 'components/menu';
import Table from 'components/table';
import Actions from 'components/table/actions';
import Modal from 'components/modal';
import Station from 'components/modal/station';
import { Button, Input } from 'components/ui/form';
import styles from './stations.module.scss';

const DEFAULT_LIMIT = 20;

const StationsPage = () => {
  const { reload, setUser, setLoading, setReload } = useStore();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [searchQuery, setSearchQuery] = useState('');
  const [edit, setEdit] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStations() {
      setLoading(true);
      const params = { page, limit };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      try {
        const response = await new StationService({ setUser }).getStations(params);

        if (cancelled || !response) return;
        if (response.items != null) {
          setItems(response.items);
          setCount(response.count ?? 0);
        }
      } catch {
        toast.error('Не удалось загрузить станции');
      } finally {
        setLoading(false);
      }
    }

    fetchStations();
    return () => { cancelled = true; };
  }, [page, limit, searchQuery, reload, setUser, setLoading]);

  const onCloseModal = (isReload = false) => {
    setEdit(null);
    setModal(false);
    if (isReload) {
      setReload();
    }
  };

  const onDelete = async () => {
    if (!deleteItem) return;
    const item = deleteItem;
    setDeleteItem(null);
    setLoading(true);
    try {
      const response = await new StationService({ setUser }).deleteStation(item.id);
      if (response) {
        toast.success(`Станция «${item.name}» удалена`);
        setReload();
      }
    } catch {
      toast.error('Не удалось удалить станцию');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const cells = [{
    name: 'ID',
    nowrap: true,
    renderField: 'id',
  }, {
    name: 'Название',
    renderField: 'name',
  }, {
    name: 'Код',
    nowrap: true,
    renderField: 'code',
  }, {
    name: 'Направление',
    wrapText: true,
    onRender: item => item.direction?.name || '—',
  }, {
    name: 'Суточное ограничение, вагонов',
    nowrap: true,
    onRender: item => item.dailyWagonLimit ?? '—',
  }, {
    name: '',
    right: true,
    nowrap: true,
    onRender: item => (
      <Actions
        data={[{
          name: 'edit',
          title: 'Редактировать',
          onClick: () => {
            setEdit(item);
            setModal(true);
          },
        }, {
          name: 'cancel',
          title: 'Удалить',
          onClick: () => setDeleteItem(item),
        }]}
      />
    ),
  }];

  return (
    <>
      <div className={styles.menu}>
        <Menu onAdd={() => setModal(true)} />
      </div>
      <div className={styles.search}>
        <Input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Поиск по названию или коду"
        />
      </div>
      <Table
        cells={cells}
        data={items}
        count={count}
        page={page}
        limit={limit}
        onPageChange={({ selected }) => setPage(selected)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
      />
      {modal ? (
        <Modal closeBackground={false} onClose={onCloseModal}>
          <Station edit={edit} onClose={onCloseModal} />
        </Modal>
      ) : null}
      {deleteItem ? (
        <Modal variant="dialog" onClose={() => setDeleteItem(null)}>
          <div className={styles.confirm}>
            <div className={styles.confirmText}>
              Вы уверены, что хотите удалить станцию
              {' '}
              <b>{deleteItem.name}</b>
              ?
            </div>
            <div className={styles.confirmActions}>
              <Button color="lightgray" onClick={() => setDeleteItem(null)}>
                Отмена
              </Button>
              <Button onClick={onDelete}>Удалить</Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default StationsPage;
